/**
 * Derive session timezone from location (country-state-city + tz-lookup).
 * Uses tz-lookup (browser-safe); geo-tz requires Node fs and does not run in Vite.
 */

import tzLookup from 'tz-lookup';
import {resolveLocationCodes} from '@/utils/locationData';

// Minimal fallback used only if the runtime lacks Intl.supportedValuesOf.
// Everything else is sourced from the runtime IANA database — no hardcoded zones.
const FALLBACK_TIMEZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
];

/**
 * All IANA time zones, sourced from the JS runtime's tz database
 * (Intl.supportedValuesOf). No hardcoded list.
 */
export function getAllTimezones(): string[] {
  try {
    const supportedValuesOf = (
      Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
      }
    ).supportedValuesOf;
    if (typeof supportedValuesOf === 'function') {
      const zones = supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length > 0) return zones;
    }
  } catch {
    // fall through to the minimal fallback below
  }
  return FALLBACK_TIMEZONES;
}

/** Current UTC offset (in minutes) for an IANA zone — used for sorting the picker. */
export function getTimezoneOffsetMinutes(
  timezone: string,
  at: Date = new Date(),
): number {
  try {
    const label = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value;
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(label ?? '');
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    const hours = Number.parseInt(match[2], 10);
    const minutes = match[3] ? Number.parseInt(match[3], 10) : 0;
    return sign * (hours * 60 + minutes);
  } catch {
    return 0;
  }
}

export interface TimezoneOption {
  value: string;
  label: string;
}

// Built once: the full IANA list is large (~400 zones) and stable per session.
let cachedTimezoneOptions: TimezoneOption[] | null = null;

/**
 * All time zones as {value, label} options, sorted by UTC offset then name.
 * Label is the human-readable form (e.g. "(UTC+5:30) IST - Asia/Kolkata").
 */
export function getTimezoneOptions(): TimezoneOption[] {
  if (cachedTimezoneOptions) return cachedTimezoneOptions;
  cachedTimezoneOptions = getAllTimezones()
    .map((tz) => ({
      value: tz,
      label: formatSessionTimezoneDisplay(tz),
      offset: getTimezoneOffsetMinutes(tz),
    }))
    .sort((a, b) => a.offset - b.offset || a.value.localeCompare(b.value))
    .map(({value, label}) => ({value, label}));
  return cachedTimezoneOptions;
}

function gmtOffsetLabelToUtc(gmtLabel: string): string {
  const match = /^GMT([+-])(\d{1,2}):(\d{2})$/.exec(gmtLabel.trim());
  if (!match) return gmtLabel;
  const hours = String(Number.parseInt(match[2], 10));
  const minutes = match[3];
  return minutes === '00'
    ? `(UTC${match[1]}${hours}:00)`
    : `(UTC${match[1]}${hours}:${minutes})`;
}

/** Abbreviation for an IANA zone at a given instant (EST/EDT, AKST/AKDT, etc.). */
export function getIanaTimezoneAbbreviation(
  timezone: string,
  at: Date = new Date(),
): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Human-readable session timezone (offset + abbreviation reflect DST at `at`).
 * Scheduling still uses the IANA id stored on the session type.
 */
export function formatSessionTimezoneDisplay(
  timezone: string,
  at: Date = new Date(),
): string {
  if (!timezone) return '';

  try {
    const offsetPart = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value;
    const abbr = getIanaTimezoneAbbreviation(timezone, at);
    const offset = offsetPart ? gmtOffsetLabelToUtc(offsetPart) : '';
    if (offset && abbr) {
      return `${offset} ${abbr} - ${timezone}`;
    }
    if (abbr) {
      return `${abbr} - ${timezone}`;
    }
  } catch {
    // Intl unavailable for this zone — fall back to the raw IANA id.
  }

  return timezone;
}

// Returns the exact IANA zone for the coordinates (e.g. Asia/Kolkata,
// Asia/Dubai). No mapping/bucketing — the resolved zone is used as-is.
function lookupTimezoneFromCoordinates(
  lat: number,
  lng: number,
): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    return tzLookup(lat, lng);
  } catch {
    return null;
  }
}

/**
 * Resolve a session timezone from country + state ISO codes using state centroid.
 */
export async function getTimezoneForState(
  countryCode: string,
  stateCode: string,
): Promise<string | null> {
  if (!countryCode || !stateCode) return null;

  const {State} = await import('country-state-city');
  const state = State.getStatesOfCountry(countryCode).find(
    (s) => s.isoCode === stateCode,
  );
  if (!state) return null;

  const lat = Number.parseFloat(state.latitude);
  const lng = Number.parseFloat(state.longitude);
  return lookupTimezoneFromCoordinates(lat, lng);
}

/**
 * Resolve session timezone from country centroid (fallback when state is absent or ambiguous).
 */
export async function getTimezoneForCountry(
  countryCode: string,
): Promise<string | null> {
  if (!countryCode) return null;

  const {Country} = await import('country-state-city');
  const country = Country.getCountryByCode(countryCode);
  if (!country) return null;

  const lat = Number.parseFloat(country.latitude);
  const lng = Number.parseFloat(country.longitude);
  return lookupTimezoneFromCoordinates(lat, lng);
}

/**
 * Resolve timezone from ISO codes: US always at state level; city refines when present;
 * country-level fallback when state does not resolve.
 */
/** Refine via city (when provided) before falling back to state-level resolution. */
async function resolveCityThenState(
  countryCode: string,
  stateCode: string,
  cityName: string | undefined,
): Promise<string | null> {
  if (cityName?.trim()) {
    const cityTz = await getTimezoneForCity(countryCode, stateCode, cityName);
    if (cityTz) return cityTz;
  }
  return getTimezoneForState(countryCode, stateCode);
}

export async function resolveTimezoneFromLocationCodes(
  countryCode: string,
  stateCode: string,
  cityName?: string,
): Promise<string | null> {
  if (!countryCode) return null;

  // US always resolves at (at least) state level.
  if (countryCode === 'US' && stateCode) {
    return resolveCityThenState(countryCode, stateCode, cityName);
  }

  if (stateCode) {
    const stateTz = await resolveCityThenState(countryCode, stateCode, cityName);
    if (stateTz) return stateTz;
  }

  return getTimezoneForCountry(countryCode);
}

/**
 * Refine timezone using city coordinates (optional; more accurate than state centroid).
 */
export async function getTimezoneForCity(
  countryCode: string,
  stateCode: string,
  cityName: string,
): Promise<string | null> {
  if (!countryCode || !stateCode || !cityName.trim()) return null;

  const {City} = await import('country-state-city');
  const city = City.getCitiesOfState(countryCode, stateCode).find(
    (c) => c.name.toLowerCase() === cityName.trim().toLowerCase(),
  );
  if (!city) return getTimezoneForState(countryCode, stateCode);

  const lat = Number.parseFloat(city.latitude);
  const lng = Number.parseFloat(city.longitude);
  const fromCoords = lookupTimezoneFromCoordinates(lat, lng);
  if (fromCoords) return fromCoords;

  return getTimezoneForState(countryCode, stateCode);
}

/**
 * Resolve session timezone from stored location names (country / state / city).
 */
export async function getTimezoneForLocationNames(
  countryName: string,
  stateName: string,
  cityName?: string,
): Promise<string | null> {
  const {countryCode, stateCode} = await resolveLocationCodes(
    countryName,
    stateName,
  );
  return resolveTimezoneFromLocationCodes(
    countryCode,
    stateCode,
    cityName,
  );
}
